import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Collapse, Dropdown, Form, Input, Modal, Popconfirm, Select, Skeleton, Space, Switch, Table, message,
} from 'antd';
import {
  CopyOutlined, MoreOutlined, SyncOutlined,
} from '@ant-design/icons';
import AppDetailModal from '../ui/AppDetailModal';
import DetailSection from '../ui/DetailSection';
import DetailStatusLine from '../ui/DetailStatusLine';
import LinkedAssistantsPanel, { LinkedAssistantsAddControl } from './LinkedAssistantsPanel';
import ToolPolicyIconSelector from './ToolPolicyIconSelector';
import { getEffectiveToolPolicy, toolKindForPolicy } from './toolsScopeUtils';
import HttpBindingEditor from './HttpBindingEditor';
import { formatSyncTime } from './toolsUtils';
import {
  DEFAULT_PARAMS_SCHEMA,
  HTTP_AUTH_OPTIONS,
  HTTP_METHODS,
  buildHttpAuth,
  parseParametersSchema,
} from './toolFormUtils';
import {
  addConnectorTool,
  connectMcpServer,
  deleteConnector,
  deleteMcpServer,
  getConnector,
  getMcpServer,
  linkAssistantConnector,
  linkAssistantMcpServer,
  removeConnectorAssistantLink,
  removeMcpServerAssistantLink,
  setConnectorAssistantLink,
  setMcpServerAssistantLink,
  startMcpOAuth,
  updateConnector,
  updateMcpServer,
} from '../../services/api/agents';
import { openMcpOAuthPopup } from '../../utils/mcpOAuth';
import { handleMcpConnectResult } from './toolFormUtils';

export default function ToolDetailDrawer({
  open,
  rowType,
  toolId,
  rowPreview,
  keycloak,
  assistants,
  assistantsLoaded,
  onClose,
  onRefreshList,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [endpointOpen, setEndpointOpen] = useState(false);
  const [editConnectionOpen, setEditConnectionOpen] = useState(false);
  const [endpointForm] = Form.useForm();
  const [connectionForm] = Form.useForm();
  const [toolSearch, setToolSearch] = useState('');

  const loadDetail = async () => {
    if (!toolId || !keycloak?.authenticated) return;
    setLoading(true);
    if (rowPreview && !detail) {
      setDetail(rowType === 'mcp' ? {
        id: toolId,
        name: rowPreview.name,
        url: rowPreview.url,
        enabled: rowPreview.enabled,
        tools: [],
        tool_policies: {},
      } : null);
    }
    try {
      const data = rowType === 'http'
        ? await getConnector(keycloak, toolId)
        : await getMcpServer(keycloak, toolId);
      setDetail(data);
    } catch (e) {
      message.error(e.message);
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && toolId) {
      setDetail(null);
      loadDetail();
    }
    if (!open) {
      setDetail(null);
      setToolSearch('');
    }
  }, [open, toolId, rowType]);

  const linkedAssistants = detail?.linked_assistants || [];
  const detailUrl = rowType === 'http' ? detail?.base_url : detail?.url;

  const handleAddAssistant = async (assistantId) => {
    if (!detail) return;
    setLinking(true);
    try {
      if (rowType === 'http') {
        await linkAssistantConnector(keycloak, assistantId, { connector_id: detail.id, enabled: true });
      } else {
        await linkAssistantMcpServer(keycloak, assistantId, { mcp_server_id: detail.id, enabled: true });
      }
      message.success('Assistant linked');
      await loadDetail();
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLinking(false);
    }
  };

  const handleToggleAssistant = async (assistantId, enabled) => {
    if (!detail) return;
    try {
      if (rowType === 'http') {
        await setConnectorAssistantLink(keycloak, detail.id, assistantId, { enabled });
      } else {
        await setMcpServerAssistantLink(keycloak, detail.id, assistantId, { enabled });
      }
      message.success(enabled ? 'Enabled for assistant' : 'Disabled for assistant');
      await loadDetail();
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleRemoveAssistant = async (assistantId) => {
    if (!detail) return;
    try {
      if (rowType === 'http') {
        await removeConnectorAssistantLink(keycloak, detail.id, assistantId);
      } else {
        await removeMcpServerAssistantLink(keycloak, detail.id, assistantId);
      }
      message.success('Removed from assistant');
      await loadDetail();
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleSyncMcp = async () => {
    if (!detail) return;
    setSyncing(true);
    try {
      const result = await connectMcpServer(keycloak, detail.id);
      handleMcpConnectResult(result, { onSuccess: () => loadDetail() });
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleMcpOAuth = async () => {
    if (!detail) return;
    setAuthorizing(true);
    try {
      const result = await startMcpOAuth(keycloak, detail.id);
      openMcpOAuthPopup(result.authorization_url, { serverId: detail.id });
    } catch (e) {
      message.error(e.message);
    } finally {
      setAuthorizing(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    try {
      if (rowType === 'http') await deleteConnector(keycloak, detail.id);
      else await deleteMcpServer(keycloak, detail.id);
      message.success('Deleted');
      onClose?.();
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleSaveConnection = async (values) => {
    if (!detail) return;
    try {
      if (rowType === 'http') {
        await updateConnector(keycloak, detail.id, {
          name: values.name,
          base_url: values.base_url,
          auth: buildHttpAuth(values),
        });
      } else {
        await updateMcpServer(keycloak, detail.id, {
          name: values.name,
          url: values.url,
        });
      }
      message.success('Connection updated');
      setEditConnectionOpen(false);
      await loadDetail();
      onRefreshList?.(true);
    } catch (e) {
      message.error(e.message);
    }
  };

  const handlePolicyChange = useCallback(async (toolName, policy) => {
    if (!detail) return;
    try {
      const payload = { tool_policies: { [toolName]: policy } };
      if (rowType === 'http') {
        await updateConnector(keycloak, detail.id, payload);
      } else {
        await updateMcpServer(keycloak, detail.id, payload);
      }
      setDetail((prev) => ({
        ...prev,
        tool_policies: { ...(prev.tool_policies || {}), [toolName]: policy },
      }));
    } catch (e) {
      message.error(e.message);
      await loadDetail();
    }
  }, [detail, rowType, keycloak]);

  const copyUrl = async () => {
    if (!detailUrl) return;
    try {
      await navigator.clipboard.writeText(detailUrl);
      message.success('URL copied');
    } catch {
      message.error('Could not copy URL');
    }
  };

  const filteredTools = useMemo(() => {
    const tools = detail?.tools || [];
    const q = toolSearch.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [detail, toolSearch]);

  const toolTableColumns = useMemo(() => {
    const permissionCol = {
      title: 'Permission',
      key: 'permission',
      width: 108,
      align: 'right',
      render: (_, tool) => {
        const kind = toolKindForPolicy(tool, rowType);
        const policy = getEffectiveToolPolicy(detail?.tool_policies, tool.name, kind);
        return (
          <ToolPolicyIconSelector
            value={policy}
            onChange={(value) => handlePolicyChange(tool.name, value)}
          />
        );
      },
    };

    if (rowType === 'http') {
      return [
        { title: 'Name', dataIndex: 'name', width: '22%', ellipsis: true },
        { title: 'Kind', dataIndex: 'kind', width: 68 },
        { title: 'Method', dataIndex: 'http_method', width: 72 },
        { title: 'Path', dataIndex: 'path_template', ellipsis: true },
        permissionCol,
      ];
    }

    return [
      { title: 'Name', dataIndex: 'name', width: '24%', ellipsis: true },
      { title: 'Description', dataIndex: 'description', ellipsis: true },
      permissionCol,
    ];
  }, [rowType, detail?.tool_policies, handlePolicyChange]);

  const needsOAuth = rowType === 'mcp'
    && (detail?.auth?.type === 'oauth_authorization_code' || !detail?.auth?.oauth_connected);

  const statusItems = useMemo(() => {
    if (!detail) return [];
    const items = [];
    if (rowType === 'mcp' && detail.sync_error) {
      items.push({ key: 'status', label: 'Error', tone: 'error' });
    } else if (rowType === 'mcp' && !detail.tools_synced_at) {
      items.push({ key: 'status', label: 'Pending sync', tone: 'warning' });
    } else {
      items.push({ key: 'status', label: 'Connected', tone: 'success' });
    }
    if (rowType === 'mcp' && detail.enabled === false) {
      items.push({ key: 'disabled', label: 'Server disabled', tone: 'muted' });
    }
    if (detail.auth?.oauth_connected) {
      items.push({ key: 'oauth', label: 'Signed in', tone: 'success' });
    }
    const toolCount = detail.tools?.length ?? 0;
    if (toolCount > 0) {
      items.push({ key: 'tools', label: `${toolCount} tools`, tone: 'muted', dot: false });
    }
    const syncLabel = formatSyncTime(detail.tools_synced_at);
    if (syncLabel) {
      items.push({ key: 'sync', label: `Synced ${syncLabel}`, tone: 'muted', dot: false });
    }
    return items;
  }, [detail, rowType]);

  const overflowMenuItems = useMemo(() => {
    if (!detail) return [];
    const items = [];
    if (rowType === 'http') {
      items.push({ key: 'add-endpoint', label: 'Add endpoint' });
    }
    if (rowType === 'mcp' && needsOAuth) {
      items.push({
        key: 'sign-in',
        label: detail.auth?.oauth_connected ? 'Sign in again' : 'Sign in',
        disabled: authorizing,
      });
    }
    if (rowType === 'mcp') {
      items.push({
        key: 'sync',
        label: 'Sync tools',
        disabled: syncing,
        icon: <SyncOutlined />,
      });
    }
    items.push({ key: 'edit-connection', label: 'Edit connection' });
    items.push({ type: 'divider' });
    items.push({
      key: 'delete',
      label: (
        <Popconfirm title="Delete permanently?" onConfirm={handleDelete} okText="Delete" okButtonProps={{ danger: true }}>
          <span style={{ color: '#cf1322' }}>Delete</span>
        </Popconfirm>
      ),
    });
    return items;
  }, [detail, rowType, needsOAuth, authorizing, syncing]);

  const handleOverflowClick = ({ key }) => {
    if (key === 'add-endpoint') {
      endpointForm.resetFields();
      setEndpointOpen(true);
      return;
    }
    if (key === 'sign-in') {
      handleMcpOAuth();
      return;
    }
    if (key === 'sync') {
      handleSyncMcp();
      return;
    }
    if (key === 'edit-connection') {
      if (rowType === 'http') {
        connectionForm.setFieldsValue({
          name: detail?.name,
          base_url: detail?.base_url,
          auth_type: detail?.auth?.type || 'none',
        });
      } else {
        connectionForm.setFieldsValue({ name: detail?.name, url: detail?.url });
      }
      setEditConnectionOpen(true);
    }
  };

  const overflowMenu = detail && !loading ? (
    <Dropdown
      menu={{ items: overflowMenuItems, onClick: handleOverflowClick }}
      trigger={['click']}
    >
      <Button type="text" icon={<MoreOutlined />} aria-label="More actions" />
    </Dropdown>
  ) : null;

  return (
    <>
      <AppDetailModal
        open={open}
        title={detail?.name || (rowType === 'http' ? 'HTTP source' : 'MCP server')}
        subtitle={rowType === 'http' ? 'HTTP API tools' : 'Remote MCP server'}
        onCancel={onClose}
        loading={loading}
        width={860}
        overflowMenu={overflowMenu}
      >
        {loading && !detail ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : detail ? (
          <>
            {detailUrl ? (
              <div className="detail-url-row">
                <span>{detailUrl}</span>
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={copyUrl} aria-label="Copy URL" />
              </div>
            ) : null}

            <DetailStatusLine items={statusItems} />

            {rowType === 'mcp' ? (
              <DetailSection
                title="Connection"
                extra={(
                  <Space size="small">
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Server enabled</span>
                    <Switch
                      checked={detail.enabled !== false}
                      loading={togglingEnabled}
                      onChange={async (checked) => {
                        setTogglingEnabled(true);
                        try {
                          await updateMcpServer(keycloak, detail.id, { enabled: checked });
                          setDetail((prev) => ({ ...prev, enabled: checked }));
                          onRefreshList?.(true);
                        } catch (e) {
                          message.error(e.message);
                        } finally {
                          setTogglingEnabled(false);
                        }
                      }}
                    />
                  </Space>
                )}
              >
                {detail.sync_error ? (
                  <Alert type="error" showIcon message="Sync error" description={detail.sync_error} />
                ) : null}
                {rowType === 'mcp' && needsOAuth && !detail.auth?.oauth_connected ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: detail.sync_error ? 12 : 0 }}
                    message="Sign in required"
                    description="This server uses OAuth. Sign in from the menu to load tools under your account."
                  />
                ) : null}
              </DetailSection>
            ) : null}

            <DetailSection
              title="Linked assistants"
              extra={(
                <LinkedAssistantsAddControl
                  linkedAssistants={linkedAssistants}
                  assistants={assistants}
                  onAdd={handleAddAssistant}
                  adding={linking}
                />
              )}
            >
              <LinkedAssistantsPanel
                linkedAssistants={linkedAssistants}
                assistants={assistants}
                assistantsLoaded={assistantsLoaded}
                onAdd={handleAddAssistant}
                onToggle={handleToggleAssistant}
                onRemove={handleRemoveAssistant}
                adding={linking}
              />
            </DetailSection>

            <DetailSection title="Tools">
              <Input
                placeholder="Search tools"
                value={toolSearch}
                onChange={(e) => setToolSearch(e.target.value)}
                style={{ marginBottom: 12, maxWidth: 280 }}
                allowClear
              />
              <Table
                size="small"
                rowKey={rowType === 'http' ? 'id' : 'name'}
                dataSource={filteredTools}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                columns={toolTableColumns}
                locale={{ emptyText: 'No tools synced yet.' }}
              />
            </DetailSection>

            {rowType === 'http' ? (
              <Collapse
                items={[{
                  key: 'binding',
                  label: 'UI binding (advanced)',
                  children: (
                    <HttpBindingEditor
                      keycloak={keycloak}
                      connectorId={detail.id}
                      tools={detail.tools}
                      onSaved={loadDetail}
                    />
                  ),
                }]}
              />
            ) : null}
          </>
        ) : null}
      </AppDetailModal>

      <Modal
        title="Add endpoint"
        open={endpointOpen}
        onCancel={() => setEndpointOpen(false)}
        onOk={() => endpointForm.submit()}
        okText="Add"
        width={560}
        destroyOnClose
      >
        <Form
          form={endpointForm}
          layout="vertical"
          initialValues={{ http_method: 'GET', kind: 'read', parameters_schema_text: DEFAULT_PARAMS_SCHEMA }}
          onFinish={async (values) => {
            try {
              const parameters_schema = parseParametersSchema(values.parameters_schema_text || DEFAULT_PARAMS_SCHEMA);
              await addConnectorTool(keycloak, detail.id, {
                name: values.tool_name,
                description: values.tool_description || values.tool_name,
                http_method: values.http_method || 'GET',
                path_template: values.path_template,
                parameters_schema,
                kind: values.kind || 'read',
              });
              message.success('Endpoint added');
              setEndpointOpen(false);
              await loadDetail();
            } catch (e) {
              message.error(e.message);
            }
          }}
        >
          <Form.Item name="tool_name" label="Tool name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tool_description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space>
            <Form.Item name="http_method" label="Method">
              <Select options={HTTP_METHODS} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="kind" label="Kind">
              <Select options={[{ value: 'read', label: 'Read' }, { value: 'write', label: 'Write' }]} style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <Form.Item name="path_template" label="Path" rules={[{ required: true }]}>
            <Input placeholder="/search" />
          </Form.Item>
          <Form.Item name="parameters_schema_text" label="Parameters JSON Schema">
            <Input.TextArea rows={6} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit connection"
        open={editConnectionOpen}
        onCancel={() => setEditConnectionOpen(false)}
        onOk={() => connectionForm.submit()}
        okText="Save"
        width={520}
        destroyOnClose
      >
        <Form form={connectionForm} layout="vertical" onFinish={handleSaveConnection}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {rowType === 'http' ? (
            <>
              <Form.Item name="base_url" label="Base URL" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="auth_type" label="Auth">
                <Select options={HTTP_AUTH_OPTIONS} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => getFieldValue('auth_type') === 'bearer' && (
                  <Form.Item name="bearer_token" label="Bearer token">
                    <Input.Password placeholder="Leave blank to keep existing" />
                  </Form.Item>
                )}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => getFieldValue('auth_type') === 'api_key' && (
                  <>
                    <Form.Item name="api_key_header" label="API key header">
                      <Input />
                    </Form.Item>
                    <Form.Item name="api_key_value" label="API key">
                      <Input.Password placeholder="Leave blank to keep existing" />
                    </Form.Item>
                  </>
                )}
              </Form.Item>
            </>
          ) : (
            <Form.Item name="url" label="MCP server URL" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
